import type { DfyEtaDto, Locale } from "../shared/dto";
import { dfyStepLabel } from "../shared/dfy-catalog";
import { clockFields, intlLanguage } from "./date-format";

/** The band is rounded OUTWARD to five minutes: an estimate that looks precise
 * to the minute promises a precision four validated runs cannot carry. Never
 * below one minute, never a time in the past. */
export function dfyEtaRangeMinutes(eta: Pick<DfyEtaDto, "remainingP25Ms" | "remainingP75Ms">): {
  from: number;
  to: number;
} {
  const from = Math.max(1, Math.floor(eta.remainingP25Ms / 60_000 / 5) * 5);
  const to = Math.max(from, Math.ceil(eta.remainingP75Ms / 60_000 / 5) * 5);
  return { from, to };
}

export function dfyReadyAtLabel(etaAt: string, locale: Locale, timeZone?: string): string | null {
  const parsed = Date.parse(etaAt);
  if (!Number.isFinite(parsed)) return null;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    ...clockFields(locale),
    ...(timeZone ? { timeZone } : {})
  }).format(new Date(parsed));
}

export interface DfyEtaLineProps {
  currentStepKey: string | null;
  currentStepNumber: number;
  totalSteps: number;
  eta?: DfyEtaDto | null;
  locale: Locale;
  t: (key: string) => string;
  /** The partner also gets the clock time; the client only needs the band. */
  showReadyAt?: boolean;
  sampleNote?: boolean;
  timeZone?: string;
  /** False when the caller already wraps this in a live region: nesting two
   * makes screen readers announce the same sentence twice. */
  live?: boolean;
}

/**
 * "Krok 11 z 15 · Sestavení stránky" plus the honest remaining band.
 *
 * One component for both surfaces: the partner and the client must never read
 * a different step or a different time for the same run. The live region holds
 * ONLY this text, so an elapsed-minutes counter ticking next to it cannot make
 * a screen reader re-announce an unchanged estimate.
 */
export function DfyEtaLine(props: DfyEtaLineProps) {
  const stepLabel = props.currentStepKey ? dfyStepLabel(props.currentStepKey, props.t) : null;
  const stepLine = props.totalSteps > 0
    ? props.t("dfyEta.step")
      .replace("{current}", String(props.currentStepNumber))
      .replace("{total}", String(props.totalSteps))
      .replace("{label}", stepLabel ?? props.t("dfyEta.finishing"))
    : null;
  const range = props.eta ? dfyEtaRangeMinutes(props.eta) : null;
  const readyAt = props.eta && props.showReadyAt
    ? dfyReadyAtLabel(props.eta.etaAt, props.locale, props.timeZone)
    : null;
  return (
    <div
      className="dfy-eta-line"
      {...(props.live === false ? {} : { role: "status", "aria-live": "polite" as const })}
      data-dfy-eta-source={props.eta?.source ?? "none"}
    >
      {stepLine && <p className="dfy-eta-step">{stepLine}</p>}
      {range ? (
        <p className="dfy-eta-range">
          {props.t("dfyEta.remainingRange")
            .replace("{from}", String(range.from))
            .replace("{to}", String(range.to))}
        </p>
      ) : (
        <p className="dfy-eta-range dfy-eta-learning">{props.t("dfyEta.learning")}</p>
      )}
      {readyAt && (
        <p className="dfy-eta-ready">{props.t("dfyEta.readyAt").replace("{time}", readyAt)}</p>
      )}
      {props.sampleNote && props.eta && (
        <p className="dfy-eta-sample">
          {props.t("dfyEta.sample").replace("{count}", String(props.eta.sampleSize))}
        </p>
      )}
    </div>
  );
}
